/**
 * One row of the contact's Alternative Information section: Name | Email | Mobile | Label.
 *
 * The API keeps alternates in three arrays, and each row is split back out on save
 * (see docs/backend/REM-3497-alternate-contacts.md):
 * - a row with a name (or one created by linking a lead, `lead_id`) is another person sharing the
 *   record → `alternate_contacts[{ name, email, mobile_no, label, lead_id }]`;
 * - a row without a name is just an extra email/mobile for this contact → the existing
 *   `secondary_emails[{ email, label }]` / `secondary_mobile[{ mobile_no, label }]`, unchanged.
 */
export interface ContactAlternativeRow {
  name: string;
  email: string;
  mobile_no: string;
  label: string;
  lead_id?: string;
}

/** An `alternate_contacts` entry — read by the lead page's Linked Contact card. */
export type AlternateContact = ContactAlternativeRow;

export function toAlternateContacts(raw: any[]): AlternateContact[] {
  return (raw ?? [])
    .filter(item => item?.name || item?.email || item?.mobile_no)
    .map(item => ({
      name: item.name ?? '',
      email: item.email ?? '',
      mobile_no: item.mobile_no ?? '',
      label: item.label ?? null,
      lead_id: item.lead_id ?? null,
    }));
}

/**
 * Rows come back grouped (people first, then extra emails, then extra mobiles) because the data
 * lives in three arrays. A name-less row holding both an email and a mobile therefore reloads as
 * two rows.
 */
export function toAlternativeRows(contact: any): ContactAlternativeRow[] {
  const people = toAlternateContacts(contact?.alternate_contacts);
  const emails = (contact?.secondary_emails ?? [])
    .filter(item => item?.email)
    .map(item => ({ name: '', email: item.email, mobile_no: '', label: item.label ?? null }));
  const mobiles = (contact?.secondary_mobile ?? [])
    .filter(item => item?.mobile_no)
    .map(item => ({ name: '', email: '', mobile_no: item.mobile_no, label: item.label ?? null }));
  return [...people, ...emails, ...mobiles];
}

export function toAlternativePayload(rows: ContactAlternativeRow[]): {
  secondary_emails: any[];
  secondary_mobile: any[];
  alternate_contacts: any[];
} {
  const filled = (rows ?? [])
    .map(row => ({
      name: row?.name?.trim() ?? '',
      email: row?.email?.trim() ?? '',
      mobile_no: row?.mobile_no?.trim() ?? '',
      label: row?.label ?? null,
      lead_id: row?.lead_id ?? null,
    }))
    .filter(row => row.name || row.email || row.mobile_no);
  const isPerson = (row: ContactAlternativeRow) => !!row.name || !!row.lead_id;
  const extras = filled.filter(row => !isPerson(row));
  return {
    secondary_emails: extras.filter(row => row.email).map(row => ({ email: row.email, label: row.label })),
    secondary_mobile: extras.filter(row => row.mobile_no).map(row => ({ mobile_no: row.mobile_no, label: row.label })),
    alternate_contacts: filled.filter(isPerson),
  };
}
